import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

const PAGE_SIZE = 20;

const formatSubDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return String(iso);
  }
};

const AdminUsersPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [tokenAdjustment, setTokenAdjustment] = useState({ amount: '', note: '', reason: '' });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-users', { page, search }],
    queryFn: async () => {
      const params = {
        page,
        page_size: PAGE_SIZE,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      const response = await adminAPI.getUsers(params);
      return response.data;
    },
    keepPreviousData: true,
  });

  const users = data?.users || [];
  const pagination = data?.pagination || { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 };

  const updateUserMutation = useMutation({
    mutationFn: ({ id, values }) => adminAPI.updateUser(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      setEditingUser(null);
      setEditValues({});
      setTokenAdjustment({ amount: '', note: '', reason: '' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id) => adminAPI.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
    },
  });

  const adjustTokensMutation = useMutation({
    mutationFn: ({ id, payload }) => adminAPI.adjustUserTokens(id, payload),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries(['admin-users']);
      const updatedBalance = response?.data?.tokenBalance;
      if (typeof updatedBalance === 'number') {
        setEditingUser((prev) =>
          prev && prev.id === variables.id ? { ...prev, tokenBalance: updatedBalance } : prev
        );
      }
      setTokenAdjustment({ amount: '', note: '', reason: '' });
    },
  });

  const handleStartEdit = (user) => {
    setEditingUser({ ...user });
    setEditValues({
      username: user.username || '',
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role || '',
      isActive: user.isActive,
      isSuperuser: user.isSuperuser,
      interests: user.interests || '',
    });
    setTokenAdjustment({ amount: '', note: '', reason: '' });
  };

  const handleEditChange = (field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = () => {
    if (!editingUser) return;
    updateUserMutation.mutate({
      id: editingUser.id,
      values: editValues,
    });
  };

  const handleApplyTokenAdjustment = () => {
    if (!editingUser) return;
    const rawAmount = Number(tokenAdjustment.amount);
    if (!Number.isFinite(rawAmount) || rawAmount === 0) {
      window.alert('Enter a non-zero token amount to adjust.');
      return;
    }
    adjustTokensMutation.mutate({
      id: editingUser.id,
      payload: {
        amount: Math.trunc(rawAmount),
        note: tokenAdjustment.note?.trim() || undefined,
        reason: tokenAdjustment.reason?.trim() || undefined,
      },
    });
  };

  const handleDelete = (user) => {
    const confirmed = window.confirm(`Deactivate user "${user.username}"?`);
    if (!confirmed) return;
    deleteUserMutation.mutate(user.id);
  };

  const canGoPrev = useMemo(() => pagination.page > 1, [pagination.page]);
  const canGoNext = useMemo(
    () => pagination.totalPages && pagination.page < pagination.totalPages,
    [pagination.page, pagination.totalPages]
  );

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Users</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View and manage all users in the system.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1 max-w-md">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Search users
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by username, email, first or last name"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Total users: <span className="font-medium">{pagination.total}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Loading users...</div>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load users.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Username</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Role</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Tokens</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Subscription</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Active</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Superuser</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Created</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                      {user.username}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                      {user.email}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || '–'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                      {user.role}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                      {typeof user.tokenBalance === 'number' ? user.tokenBalance : 0}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                      {(() => {
                        const sub = user?.subscription;
                        const isActivePlan = !!sub?.isActivePlan;
                        const badgeClass = isActivePlan
                          ? 'inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-900 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700'
                          : 'inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700';

                        const statusLabel = isActivePlan
                          ? 'Active'
                          : sub
                            ? (sub.status || '—')
                            : 'None';

                        const planLabel = sub?.planId && sub.planId !== 'free' ? sub.planId : 'free';
                        const providerLabel = sub?.provider ? String(sub.provider).toUpperCase() : null;
                        const endLabel = formatSubDate(sub?.currentPeriodEnd);

                        return (
                          <div className="flex flex-col">
                            <span className={badgeClass}>{statusLabel}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {planLabel}
                              {providerLabel ? ` • ${providerLabel}` : ''}
                              {endLabel ? ` • until ${endLabel}` : ''}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={
                          user.isActive
                            ? 'inline-flex items-center rounded-full bg-green-50 dark:bg-green-900 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
                            : 'inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                        }
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={
                          user.isSuperuser
                            ? 'inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                            : 'inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                        }
                      >
                        {user.isSuperuser ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400 text-xs">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '–'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(user)}
                        className="inline-flex items-center rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 mr-2"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(user)}
                        className="inline-flex items-center rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                        disabled={deleteUserMutation.isLoading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages || 1}
            </div>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => canGoPrev && setPage((p) => Math.max(1, p - 1))}
                disabled={!canGoPrev}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoPrev
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setPage((p) => p + 1)}
                disabled={!canGoNext}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoNext
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {editingUser && (
        <div className="mt-8 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Edit user: {editingUser.username}
            </h2>
            <button
              type="button"
              onClick={() => {
                setEditingUser(null);
                setEditValues({});
                setTokenAdjustment({ amount: '', note: '', reason: '' });
              }}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={editValues.username || ''}
                onChange={(e) => handleEditChange('username', e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={editValues.email || ''}
                onChange={(e) => handleEditChange('email', e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                First name
              </label>
              <input
                type="text"
                value={editValues.firstName || ''}
                onChange={(e) => handleEditChange('firstName', e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last name
              </label>
              <input
                type="text"
                value={editValues.lastName || ''}
                onChange={(e) => handleEditChange('lastName', e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <input
                type="text"
                value={editValues.role || ''}
                onChange={(e) => handleEditChange('role', e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-6 mt-5">
              <label className="inline-flex items-center text-xs text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  checked={!!editValues.isActive}
                  onChange={(e) => handleEditChange('isActive', e.target.checked)}
                />
                <span className="ml-2">Active</span>
              </label>
              <label className="inline-flex items-center text-xs text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  checked={!!editValues.isSuperuser}
                  onChange={(e) =>
                    handleEditChange('isSuperuser', e.target.checked)
                  }
                />
                <span className="ml-2">Superuser</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Interests
              </label>
              <textarea
                value={editValues.interests || ''}
                onChange={(e) => handleEditChange('interests', e.target.value)}
                rows={2}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
          </div>
          <div className="md:col-span-2 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Token balance</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Positive values add tokens, negative subtract.</p>
              </div>
              <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {typeof editingUser.tokenBalance === 'number'
                  ? editingUser.tokenBalance.toLocaleString()
                  : '0'}
              </div>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step="1"
                  value={tokenAdjustment.amount}
                  onChange={(e) =>
                    setTokenAdjustment((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  placeholder="e.g. 100 or -25"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={tokenAdjustment.reason}
                  onChange={(e) =>
                    setTokenAdjustment((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder="e.g. manual credit"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Note (optional)
                </label>
                <input
                  type="text"
                  value={tokenAdjustment.note}
                  onChange={(e) =>
                    setTokenAdjustment((prev) => ({ ...prev, note: e.target.value }))
                  }
                  placeholder="Visible in ledger metadata"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            {adjustTokensMutation.isError && (
              <div className="mt-3 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-200">
                {(() => {
                  const detail = adjustTokensMutation.error?.response?.data?.detail;
                  if (typeof detail === 'string') return detail;
                  if (detail && typeof detail === 'object' && detail.tokensRequired !== undefined) {
                    return `Insufficient balance: needs ${detail.tokensRequired} tokens but user has ${detail.tokensRemaining}.`;
                  }
                  return adjustTokensMutation.error?.message || 'Failed to adjust tokens.';
                })()}
              </div>
            )}
            <div className="mt-3">
              <button
                type="button"
                onClick={handleApplyTokenAdjustment}
                disabled={adjustTokensMutation.isLoading}
                className="inline-flex items-center rounded border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {adjustTokensMutation.isLoading ? 'Updating...' : 'Apply token adjustment'}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => {
              setEditingUser(null);
              setEditValues({});
              setTokenAdjustment({ amount: '', note: '', reason: '' });
            }}
            className="inline-flex items-center rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={updateUserMutation.isLoading}
              className="inline-flex items-center rounded border border-blue-600 bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;

